import React, { useEffect, useState } from 'react';
import { useCart } from '../contexts/CartContext';
import apiService, { toThumbSrc } from '../services/api';
import analytics from '../services/analytics';

type Props = { slug: string };

const ProductCard: React.FC<Props> = ({ slug }) => {
  const { addItem, isInCart, getItemQuantity } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<any>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiService.getProduct(slug);
        if (!mounted) return;
        setProduct(data);
      } catch (e: any) {
        console.error('Failed to load product', slug, e);
        setError('Kunde inte ladda produkt');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [slug]);

  const handleAdd = () => {
    if (!product) return;
    try {
      setAdding(true);
      addItem(product.id, 1);
      analytics.trackAddToCart(product.id, product.name, 1, Number(product.final_price ?? product.price));
      // adding state will relax automatically on next render
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return <div className="product-card loading">Laddar…</div>;
  }
  if (error || !product) {
    return <div className="product-card error">{error || 'Produkten kunde inte visas'}</div>;
  }

  const inCart = isInCart(product.id);
  const qty = getItemQuantity(product.id);
  const href = `/produkter/${product.slug}`;
  const img = product.images && product.images.length ? product.images[0] : '';
  const price = product.final_price ?? product.price;

  return (
    <div className="product-card">
      <a className="product-thumb" href={href}>
        <img src={toThumbSrc(img)} alt={product.name} loading="lazy" />
      </a>
      <div className="product-info">
        <a className="product-name" href={href}>{product.name}</a>
        <div className="product-price">{price?.toFixed(2)} kr</div>
        {product.inventory_quantity <= 0 && (
          <div className="product-stock out">Slut i lager</div>
        )}
        {product.inventory_quantity > 0 && (
          <div className="product-actions">
            <button
              className="add-to-cart"
              onClick={handleAdd}
              disabled={adding}
              aria-label="Lägg i kundvagn"
            >
              {inCart ? `I kundvagn (${qty})` : 'Lägg i kundvagn'}
            </button>
            <a className="details-link" href={href}>Visa detaljer</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
